// src/components/compliance/modals/AcceptedRecommendationsModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useState } from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "No connector")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function formatRefreshError(error) {
  const text = String(error || "").trim();
  if (!text) return "";

  if (text.toLowerCase() === "failed to fetch") {
    return "Accepted recommendations could not be refreshed. Check the API connection and try again.";
  }

  return text;
}

function getAcceptedAt(item) {
  return item.accepted_at || item.updated_at || item.created_at || item.shown_at || null;
}

const summaryBarStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(15, 23, 42, 0.44)",
  color: "#cbd5e1",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const summaryCountStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.45,
};

const listStyle = {
  display: "grid",
  gap: 12,
};

const itemCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.46)",
  display: "grid",
  gap: 12,
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(30, 41, 59, 0.64)",
  border: "1px solid rgba(148, 163, 184, 0.20)",
  color: "#cbd5e1",
};

const acceptedChipStyle = {
  ...chipStyle,
  color: "#bfdbfe",
  borderColor: "rgba(96, 165, 250, 0.30)",
  background: "rgba(30, 64, 175, 0.14)",
};

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  color: "#cbd5e1",
  fontSize: 13,
};

const bodyTextStyle = {
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.48)",
  color: "#cbd5e1",
  fontSize: 13,
  lineHeight: 1.45,
};

const auditNoticeStyle = {
  ...noticeStyle,
  border: "1px solid rgba(251, 191, 36, 0.28)",
  background: "rgba(120, 53, 15, 0.12)",
  color: "#fde68a",
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

export default function AcceptedRecommendationsModal({
  open,
  items = [],
  count = 0,
  loading = false,
  error = "",
  message = "",
  restoringRecommendationId = null,
  preparingHandoffRecommendationId = null,
  onClose,
  onRefresh,
  onRestore,
  onPrepareHandoff,
}) {
  const acceptedItems = Array.isArray(items) ? items : [];
  const acceptedCount = Number.isFinite(Number(count)) ? Number(count) : acceptedItems.length;
  const formattedError = formatRefreshError(error);
  const [refreshPulse, setRefreshPulse] = useState(false);
  const isRefreshing = Boolean(loading || refreshPulse);

  useEffect(() => {
    if (!open) {
      setRefreshPulse(false);
    }
  }, [open]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;

    setRefreshPulse(true);
    onRefresh?.();

    window.setTimeout(() => {
      setRefreshPulse(false);
    }, 1200);
  }, [isRefreshing, onRefresh]);

  return (
    <ModalShell
      open={open}
      ariaLabel="Accepted recommendations"
      eyebrow="OASYS · Recommendation Recall"
      title="Accepted Recommendations"
      subtitle="Review recommendations where an operator recorded intent. Acceptance does not execute remediation or advance workflow."
      onClose={onClose}
      maxWidth="960px"
      accentBorder="rgba(96, 165, 250, 0.42)"
      zIndex={165}
    >
      <div style={summaryBarStyle}>
        <div>
          <div style={summaryCountStyle}>
            {acceptedCount} accepted recommendation{acceptedCount === 1 ? "" : "s"} available for recall
          </div>
          <div style={summaryHintStyle}>
            {isRefreshing
              ? "Refreshing accepted recommendations…"
              : "Accepted recommendations are operator-intent records. They remain advisory until restored or prepared as handoffs."}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ whiteSpace: "nowrap" }}
        >
          {isRefreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {message ? <div style={noticeStyle}>{message}</div> : null}
      {formattedError && !isRefreshing ? <div style={errorStyle}>{formattedError}</div> : null}

      <ModalSection
        title="Accepted recommendation recall"
        description="These recommendations are hidden from active guidance until restored or prepared into a future handoff."
        badge={isRefreshing ? "Refreshing" : `${acceptedItems.length} accepted`}
        badgeTone="medium"
      >
        {isRefreshing ? (
          <p style={bodyTextStyle}>Refreshing accepted recommendations…</p>
        ) : acceptedItems.length === 0 ? (
          <p style={bodyTextStyle}>No accepted recommendations are available yet.</p>
        ) : (
          <div style={listStyle}>
            {acceptedItems.map((item) => {
              const priority = formatPriority(item.priority);
              const connector = item.connector ? formatConnectorProvider(item.connector) : "No connector";
              const recommendationId = item.id || item.recommendation_id;
              const restoring = restoringRecommendationId === recommendationId;
              const preparing = preparingHandoffRecommendationId === recommendationId;

              return (
                <article key={recommendationId || item.recommendation_key || item.title} style={itemCardStyle}>
                  <div style={chipRowStyle}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={acceptedChipStyle}>Accepted</span>
                      <span
                        style={{
                          ...chipStyle,
                          color: priorityColor(item.priority),
                          borderColor: priorityColor(item.priority),
                          background: "rgba(15, 23, 42, 0.36)",
                        }}
                      >
                        {priority}
                      </span>
                      <span style={chipStyle}>{titleCaseLabel(item.category || item.surface || "Recommendation")}</span>
                      <span style={chipStyle}>{connector}</span>
                    </div>
                    <strong style={{ color: "#cbd5e1", fontSize: 12 }}>
                      Accepted {formatDateTime(getAcceptedAt(item))}
                    </strong>
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <strong style={{ color: "#f8fafc", fontSize: 16, lineHeight: 1.35 }}>
                      {item.title || item.recommendation_title || "Accepted recommendation"}
                    </strong>
                    <p style={bodyTextStyle}>
                      {item.summary || item.reason || item.description || "Operator accepted this recommendation for future review."}
                    </p>
                  </div>

                  <div style={detailGridStyle}>
                    <div>
                      <strong>Framework:</strong> {item.framework || "—"}
                    </div>
                    <div>
                      <strong>Controls:</strong> {formatList(item.controls)}
                    </div>
                    <div>
                      <strong>Findings:</strong> {formatList(item.findings)}
                    </div>
                    <div>
                      <strong>Resources:</strong> {formatList(item.resources)}
                    </div>
                  </div>

                  <div style={auditNoticeStyle}>
                    Operator intent is recorded only. OASYS has not executed remediation, closed tasks, approved evidence, advanced workflow, or run validation.
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => onPrepareHandoff?.(item)}
                      disabled={preparing || restoring}
                    >
                      {preparing ? "Preparing handoff…" : "Prepare handoff preview"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onRestore?.(item)}
                      disabled={preparing || restoring}
                    >
                      {restoring ? "Restoring…" : "Restore to active"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </ModalSection>

      <ModalActionRow
        actions={[
          {
            key: "close",
            label: "Close",
            className: "btn btn-primary btn-sm",
            onClick: onClose,
          },
        ]}
      />
    </ModalShell>
  );
}
